import { AuthorizedContent } from '@frontegg/react';
import { Tooltip } from '@mui/material';
import React, { FC, useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import { v4 as uuidv4 } from 'uuid';
import getApiChatbotResponse from '../../../api/getApiChatbotResponse';
import { useDashboardMode } from '../../../contexts/dashboardModeContext';
import { useLanguage } from '../../../contexts/languageContext';
import { TenantContext } from '../../../contexts/tenantContext';
import { colors, fontSize, fontWeight, TypographyStyledGlobal } from '../../../theme';
import { EditIconStyled } from '../../../theme/style';
import AlertType from '../../../utils/enum/AlertType';
import RolesType from '../../../utils/enum/RolesType';
import useFetcher from '../../../utils/hooks/useFetcher';
import Loader from '../../Loader/loader';
import Message from '../../Message/Message';
import DeleteIndicatorModal from '../../Modals/ConfirmationModal/DeleteIndicator';
import { getLastPageForEditKpi, CHART_INDICATOR_NAME, COMPARABLE_CHART_INDICATORS, TEMPORAL_COMPARABLE_CHART_INDICATORS, GLOBAL_COMPARISON } from '../../Modals/DashboardModal/constants';
import DashboardModal from '../../Modals/DashboardModal/dashboardModal';
import { ChatingProps } from '../../Modals/DashboardModal/dashboardModalInterface';
import EditIndicatorModal from '../../Modals/EditModal/EditIndicator';
import ActionsCard from '../ActionsCard/actionsCard';
import { ChatProps, ClauseFilterProps, DashboardParams, DateRangeScopeProps, Indicator, ScopeProps } from '../dashboardInterfaces';
import EvolutionLegend from '../EvolutionLegend/evolutionLegend';
import { IndicatorCardStyled, IndicatorCardHeader, IndicatorCardHeaderBase, ButtonCardStyled, ButtonDivStyled } from '../styled';
import { getEvolutionLegendKind } from './evolution';

interface IndicatorCardProps {
    indicator: Indicator
    selectIndicatorCard: (additionalVars: Indicator['additionalVars'], displayType: Indicator['displayType'], data: Indicator['data'], customTitle: Indicator['customItemTitle'], question: Indicator['question'], getChatbotResponse: (isSummary: boolean, question: string) => void, conversation: Array<ChatingProps>, summary: string, isSummaryDisplayed: boolean, waitingForChatAnswer: boolean, handleIsRenderedChat: (chatId: number) => void, surveyName: string | undefined, dashboardName: string | undefined) => any,
    selectedClauseFilter: Array<ClauseFilterProps>
    selectedScope: Array<ScopeProps>
    selectedDateRange: DateRangeScopeProps | null
    isComparedToGlobal?: boolean
    comparedScope?: Array<ScopeProps>
    comparedDateRange?: DateRangeScopeProps | null
    isDragging? : boolean
    dragHandle?: React.ReactNode
    surveyName?: string
    dashboardName?: string
}

const IndicatorCard: FC<IndicatorCardProps> = ({ indicator, selectIndicatorCard, selectedClauseFilter = [], selectedScope = [], selectedDateRange, isComparedToGlobal = false, comparedScope = [], comparedDateRange, isDragging = false, dragHandle, surveyName, dashboardName }) => {
    const { t } = useTranslation('dashboard');
    const { surveyId, dashboardId } = useParams<DashboardParams>();
    const [selectedSchema] = useContext(TenantContext) ?? [];
    const [conversation, setConversation] = useState<Array<ChatingProps>>([]);
    const [isChatStarted, setIsChatStarted] = useState<boolean>(true);
    const [chatId, setChatId] = useState<string>(uuidv4());
    const [summary, setSummary] = useState<string>('');
    const clauseFilters = JSON.stringify(selectedClauseFilter.map((clauseFilter: ClauseFilterProps) => ({ [clauseFilter.filterId]: clauseFilter.modalityId.map((modalityId: string) => parseInt(modalityId, 10)) })));
    const selectedScopes = JSON.stringify(selectedScope.map((scope: ScopeProps) => ({ [scope.scopeId]: scope.modalityId.map((modalityId: string) => parseInt(modalityId, 10)) })));
    const selectedDateRanges = JSON.stringify({ startDate: selectedDateRange?.startDate.toLocaleDateString('fr-CA'), endDate: selectedDateRange?.endDate.toLocaleDateString('fr-CA'), key: selectedDateRange?.key });
    const { selectedLanguage } = useLanguage();
    const selectedLanguageForSurvey = selectedLanguage && selectedLanguage.surveysLanguagesIds.find((surveyLanguage) => String(surveyLanguage.surveyId) === surveyId);
    const isTemporalComparable = TEMPORAL_COMPARABLE_CHART_INDICATORS.includes(indicator.displayType);
    const comparedDateRanges = comparedDateRange
        ? { startDate: comparedDateRange.startDate.toLocaleDateString('fr-CA'), endDate: comparedDateRange.endDate.toLocaleDateString('fr-CA') }
        : null;
    const periodComparison = (() => {
        if (!isTemporalComparable) {
            return null;
        }
        if (comparedDateRanges) {
            return { type: 'TEMPORAL', label: 'Période comparée', dateRanges: comparedDateRanges };
        }
        if (comparedScope.length > 0) {
            return { type: 'TEMPORAL', label: 'Période comparée', scopes: comparedScope.map((scope: ScopeProps) => ({ [scope.scopeId]: scope.modalityId.map((modalityId: string) => parseInt(modalityId, 10)) })) };
        }

        return null;
    })();
    const isGlobalComparable = isComparedToGlobal && COMPARABLE_CHART_INDICATORS.includes(indicator.displayType);
    // The endpoint takes a single comparison, and the two toggles are mutually
    // exclusive, so at most one of these is ever set.
    const selectedComparison = periodComparison ?? (isGlobalComparable ? GLOBAL_COMPARISON : null);
    const globalComparison = selectedComparison ? JSON.stringify(selectedComparison) : '';
    const filterScopeArray = [selectedClauseFilter.length > 0 ? `filters=${clauseFilters}` : '', selectedScope.length > 0 ? `scopes=${selectedScopes}` : '', selectedDateRange ? `dateRanges=${selectedDateRanges}` : '', (selectedLanguageForSurvey && !selectedLanguageForSurvey.isDefault) ? `translate=${selectedLanguageForSurvey.languageId}` : '', globalComparison ? `comparison=${encodeURIComponent(globalComparison)}` : ''].filter((fs: string) => fs !== '');
    const filterScopeLink = filterScopeArray.length > 0 ? `/?${filterScopeArray.join('&')}` : '';
    const { data } = useSWR(indicator.displayType !== CHART_INDICATOR_NAME.CHATBOT ? `${process.env.REACT_APP_API_URL}/survey/${surveyId}/dashboard/${dashboardId}/compute/${indicator.id}/${indicator.displayType}${filterScopeLink}` : '', useFetcher()) ?? {};
    const [isSummaryDisplayed, setIsSummaryDisplayed] = useState<boolean>(false);
    const [waitingForChatAnswer, setWaitingForChatAnswer] = useState<boolean>(false);
    const { isCreationMode } = useDashboardMode();

    useEffect(() => {
        setSummary('');
        setChatId(uuidv4());
        setIsChatStarted(true);
        setConversation([]);
        setIsSummaryDisplayed(false);
        setWaitingForChatAnswer(false);
    }, [selectedClauseFilter, selectedScope, selectedDateRange]);

    const getChatbotResponse = async (isSummary: boolean, question: string) => {
        const idConversation = conversation.length + 1;
        if (!isSummary) {
            setConversation((prev) => [...prev, { id: idConversation, entry: question, answer: '...' }]);
        } else {
            setSummary('');
            setIsSummaryDisplayed(true);
        }
        setWaitingForChatAnswer(true);
        const chat: ChatProps = { starter: isChatStarted, isSummary, question, language: selectedLanguage?.label };
        const chatResponse = await getApiChatbotResponse(surveyId, dashboardId, indicator.id, filterScopeLink, chatId, JSON.stringify(chat), selectedSchema || '');
        if (!isSummary) {
            setConversation((prev) => [...prev.slice(0, -1), { id: idConversation, entry: chat.question, answer: chatResponse.response }]);
        } else {
            setSummary(chatResponse.response);
        }
        setWaitingForChatAnswer(false);
        setIsChatStarted(false);
    };

    const handleIsRenderedChat = (chatIdRendered: number) => {
        const indexOfRederedChat = conversation.findIndex((c: ChatingProps) => (c.id === chatIdRendered));
        conversation[indexOfRederedChat].alreadyRendered = true;
    };

    const content = selectIndicatorCard(indicator.additionalVars, indicator.displayType, data, indicator.customItemTitle, indicator.question, getChatbotResponse, conversation, summary, isSummaryDisplayed, waitingForChatAnswer, handleIsRenderedChat, surveyName, dashboardName);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);

    const handleDeleteCard = () => {
        setOpenDeleteModal(true);
    };
    const handleEditTitleCard = () => {
        setOpenEditModal(true);
    };

    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const { data: editIndicator } = useSWR(isEdit ? `${process.env.REACT_APP_API_URL}/survey/${surveyId}/dashboard/${dashboardId}/indicator/${indicator.id}${(selectedLanguageForSurvey && !selectedLanguageForSurvey.isDefault) ? `/?translate=${selectedLanguageForSurvey.languageId}` : ''}` : null, useFetcher());
    const { data: editQuestion } = useSWR(editIndicator ? `${process.env.REACT_APP_API_URL}/survey/${surveyId}/dashboard/${dashboardId}/questions/${editIndicator.questionId}` : null, useFetcher());
    const { data: editCrossingQuestion } = useSWR((editIndicator && editIndicator.displayType === CHART_INDICATOR_NAME.CROSS_TABLE) ? `${process.env.REACT_APP_API_URL}/survey/${surveyId}/dashboard/${dashboardId}/questions/${editIndicator.questionId}` : null, useFetcher());
    const questionTitle = indicator.customItemTitle || ((data?.crossing_question_label ? `${indicator.question.label} X ${data.crossing_question_label}` : indicator.question.label));
    // Only a card showing at least one significant gap explains its icons.
    const evolutionLegendKind = getEvolutionLegendKind(data);

    return (
        <IndicatorCardStyled>
            <IndicatorCardHeader>
                <Tooltip
                    componentsProps={{
                        tooltip: {
                            sx: {
                                bgcolor: colors.grey100,
                                borderRadius: '4px',
                                color: colors.grey000,
                                fontSize: fontSize.sm,
                                fontWeight: fontWeight.semiRegular,
                                maxWidth: 360,
                                padding: '6px 10px',
                                '& .MuiTooltip-arrow': { color: colors.grey100 },
                            },
                        },
                    }}
                    placement="top"
                    title={indicator.question.label}
                    arrow
                >
                    <TypographyStyledGlobal size={fontSize.um} style={{ cursor: 'help' }} weight={fontWeight.semiBold}>
                        {questionTitle}
                        {isCreationMode && !isDragging && (
                            <AuthorizedContent requiredRoles={[RolesType.ADMIN, RolesType.ADMIN_MOAI]}>
                                <ButtonCardStyled onClick={handleEditTitleCard} variant='text'>({t('edit')})</ButtonCardStyled>
                            </AuthorizedContent>
                        )}
                    </TypographyStyledGlobal>
                </Tooltip>
                <ButtonDivStyled>
                    {dragHandle}
                    {isCreationMode && !isDragging && (
                        <AuthorizedContent requiredRoles={[RolesType.ADMIN, RolesType.ADMIN_MOAI]}>
                            <EditIconStyled
                                onClick={() => {
                                    setOpenModal(true);
                                    setIsEdit(true);
                                }}
                                sx={{ width: '0.8em', height: '0.92em' }}
                            />
                            <ActionsCard handleDeleteCard={handleDeleteCard} />
                        </AuthorizedContent>
                    )}
                </ButtonDivStyled>
            </IndicatorCardHeader>
            {!isDragging && indicator.displayType !== CHART_INDICATOR_NAME.CHATBOT && (
                <IndicatorCardHeaderBase>
                    <TypographyStyledGlobal color={colors.grey400} fontStyle='italic' size={fontSize.sm} weight={fontWeight.semiRegular}>
                        { t('base', { count: data?.base }) }
                    </TypographyStyledGlobal>
                </IndicatorCardHeaderBase>
            )}
            {!isDragging && data?.error && <Message content={data.error} messageType={AlertType.WARNING} />}
            {(!isDragging && content) ?? <Loader size={50} />}
            {!isDragging && evolutionLegendKind && <EvolutionLegend kind={evolutionLegendKind} />}
            <DeleteIndicatorModal indicator={indicator} openModal={openDeleteModal} setOpenModal={setOpenDeleteModal} />
            <EditIndicatorModal indicator={indicator} openModal={openEditModal} questionLabel={questionTitle} setOpenModal={setOpenEditModal} />
            <DashboardModal defaultView={getLastPageForEditKpi(indicator.displayType)} editCrossingQuestion={editCrossingQuestion} editIndicator={editIndicator} editQuestion={editQuestion} isEdit={isEdit} openModal={openModal} rankingParameter={{ columnToApply: 0, rankToApply: 0 }} setOpenModal={setOpenModal} />
        </IndicatorCardStyled>
    );
};

export default IndicatorCard;
